// ─────────────────────────────────────────────────────────────────────────────
// Salesforce Events — Mappers (DRY)
// Funções puras para montar payloads a partir de modelos do app.
// ─────────────────────────────────────────────────────────────────────────────

import { Cart } from "@app/models/cart";
import { Product, ProductAttribute } from "@app/models/product";
import { User, WeekDay, Shift } from "@app/models/user";
import { businesses, KEYS, WEEK_DAYS, SHIFTS } from "@app/models/business";
import { convertDateFromServer } from "@app/util/date";
import {
  SfAdicionarCarrinhoParams,
  SfBuscaFiltroParams,
  SfCadastroEmpresaParams,
  SfCadastroParams,
  SfCarrinhoParams,
  SfPageViewNomePagina,
  SfRemoverCarrinhoParams,
} from "./salesforce-events.types";

/** Nomes de página aceitos pelo evento sf_page_view (spec SF). */
export const SF_PAGE_VIEW_NAMES: Record<string, SfPageViewNomePagina> = {
  HOME: "Home",
  CARRINHO: "Carrinho",
  CUPONS: "Cupons",
  PEDIDOS: "Pedidos",
  ASSINATURA: "Assinatura",
  FAVORITOS: "Favoritos",
};

/**
 * Cupons, Assinatura e Favoritos: rotas ainda não existem no app.
 * Estender routeToSfPageViewNome quando as telas forem criadas.
 */
export const SF_PAGE_VIEW_PENDING_ROUTES = ["Cupons", "Assinatura", "Favoritos"] as const;

const PAGE_VIEW_DEDUP_MS = 1500;

let lastPageViewPath = "";
let lastPageViewTime = 0;

/** Apenas para testes — reinicia deduplicação de page view. */
export function resetSfPageViewDedupForTests(): void {
  lastPageViewPath = "";
  lastPageViewTime = 0;
}

/**
 * Mapeia rota Angular para nome_pagina do sf_page_view.
 * Retorna null se a rota não for uma das páginas rastreadas.
 */
export function routeToSfPageViewNome(path: string): SfPageViewNomePagina | null {
  const normalized = (path || "").split("?")[0] || "/";

  if (normalized === "/tabs/home") {
    return SF_PAGE_VIEW_NAMES.HOME;
  }
  if (normalized === "/tabs/cart" || normalized === "/tabs/cart/editing") {
    return SF_PAGE_VIEW_NAMES.CARRINHO;
  }
  if (normalized === "/tabs/orders") {
    return SF_PAGE_VIEW_NAMES.PEDIDOS;
  }

  return null;
}

/** Evita disparos duplicados na mesma rota em navegações rápidas. */
export function shouldTrackSfPageView(path: string): boolean {
  const nome = routeToSfPageViewNome(path);
  if (!nome) {
    return false;
  }
  const normalized = (path || "").split("?")[0] || "/";
  const now = Date.now();
  if (normalized === lastPageViewPath && now - lastPageViewTime < PAGE_VIEW_DEDUP_MS) {
    return false;
  }
  lastPageViewPath = normalized;
  lastPageViewTime = now;
  return true;
}

/** Contexto mínimo de catálogo para sf_busca_filtro (evita acoplamento ao CartService). */
export interface SfCatalogFilterContext {
  currentCategory?: ProductAttribute | null;
  currentBrand?: ProductAttribute | null;
  currentFlavor?: ProductAttribute | null;
}

function parseUnitsPerPackage(quantityPerPackage?: string): number | undefined {
  if (!quantityPerPackage) {
    return undefined;
  }
  const match = String(quantityPerPackage).match(/^(\d+)/);
  return match ? Number(match[1]) : undefined;
}

function resolveNomeCupom(cart: Cart): string | undefined {
  const promotionNames =
    cart.cartPromotions
      ?.map((p) => p.name || p.label || p.sku)
      .filter(Boolean)
      .join(", ") || undefined;
  if (promotionNames) {
    return promotionNames;
  }
  if (cart.promotions?.length) {
    return cart.promotions.map((p) => p.sku).filter(Boolean).join(", ");
  }
  return undefined;
}

/**
 * Monta params do sf_carrinho a partir do carrinho atual.
 * Sempre retorna objeto: campos sem valor são completados no track() ("" / 0).
 */
export function buildSfCarrinhoParams(cart: Cart): SfCarrinhoParams {
  const items = cart.cartItems ?? [];
  if (!items.length) {
    return {
      id_carrinho: sfString(cart.cartToken),
      valor_frete: cart.shipping,
    };
  }

  const shipping = cart.shipping ?? 0;
  const total = cart.total ?? 0;
  const valorTotalSemFrete = total - shipping;
  const quantidadeTotal = items.reduce((sum, item) => sum + (item.quantity ?? 0), 0);
  const subtotal = cart.subtotal ?? valorTotalSemFrete;
  const valorPorUnidade = quantidadeTotal > 0 ? subtotal / quantidadeTotal : undefined;

  const nomes = items
    .map((item) => item.product?.name || item.product?.code)
    .filter(Boolean)
    .join(", ");

  const firstItem = items[0];
  const unidade =
    parseUnitsPerPackage(firstItem.product?.quantityPerPackage) ?? quantidadeTotal;

  return {
    id_carrinho: cart.cartToken,
    valor_total_sem_frete: valorTotalSemFrete,
    valor_frete: shipping,
    valor_por_unidade: valorPorUnidade,
    nome: nomes,
    nome_cupom: resolveNomeCupom(cart),
    unidade,
    quantidade_total: quantidadeTotal,
  };
}

/** Monta params do sf_busca_filtro a partir do estado de filtros do catálogo. */
export function buildSfBuscaFiltroParams(ctx: SfCatalogFilterContext): SfBuscaFiltroParams {
  const category = ctx.currentCategory;
  const brand = ctx.currentBrand;
  const flavor = ctx.currentFlavor;

  // O catálogo tem um único nível acima da marca — `Product.categories[0]`, que o próprio
  // modelo chama de "Categoria" (ex.: Refrigerantes). Ele alimenta departamento E categoria:
  // o SF cobra os dois campos e o app não tem uma hierarquia de departamento separada, então
  // repetir o rótulo real é mais fiel do que mandar categoria sempre vazia.
  const categoriaLabel = sfString(category?.label);

  return {
    departamento: categoriaLabel,
    categoria: categoriaLabel,
    subcategoria: sfString(flavor?.label),
    marca: sfString(brand?.label),
    /** Sem atributo de cor no app; sabor ≠ cor. Vai como "" pelo fallback do schema. */
    nome_da_cor: undefined,
  };
}

export interface SfCartProductContext {
  deliveryCep?: string;
  /** Marca da navegação do catálogo (ex.: ADES), quando o item da API não traz `product.brand`. */
  catalogBrand?: string;
}

/** Só envia string com conteúdo; vazio evita ruído no SF (sanitize também remove). */
function sfString(value?: string | null): string | undefined {
  const trimmed = value?.trim();
  return trimmed || undefined;
}

/**
 * Marca do produto para eventos SF (alinhado ao GA4 `item_brand`).
 * Ordem: brand no produto → categories[1] (marca na API) → combo → marca do catálogo atual.
 * Em operações dentro do carrinho o CartService reidrata `brand` a partir da memória de
 * facetas gravada no add (`AnalyticsMapperService.rememberProductFacets`), já que a API
 * do carrinho não devolve marca por item.
 */
export function resolveProductMarca(product: Product, ctx?: SfCartProductContext): string | undefined {
  const fromProduct =
    sfString(product.brand?.label) || sfString(product.categories?.[1]?.label);

  if (fromProduct) {
    return fromProduct;
  }

  const firstItemLabel = product.items?.[0]?.label;
  if (typeof firstItemLabel === "string" && firstItemLabel.trim()) {
    const fromCombo = sfString(firstItemLabel.split(/\s+/)[0]);
    if (fromCombo) {
      return fromCombo;
    }
  }

  return sfString(ctx?.catalogBrand);
}

/** Mesma heurística do GA4: API pode enviar preço em centavos (ex.: 2673 → 26.73). */
function normalizePrecoValor(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  if (value === 0) {
    return 0;
  }
  return value > 1000 ? value / 100 : value;
}

function roundPreco(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Preço do produto/pacote para SF, não o valor por unidade.
 * Inclui faixa escalonada ativa (`currentScalable` / `scalablePrices`).
 */
export function resolveProductValor(product: Product): number | undefined {
  const activeScalable =
    product.currentScalable ?? product.scalablePrices?.find((s) => s.active);

  const raw =
    product.price ??
    activeScalable?.price ??
    product.scalablePrices?.[0]?.price ??
    product.unitPrice ??
    activeScalable?.unitPrice ??
    product.scalablePrices?.[0]?.unitPrice;

  if (raw === undefined || raw === null) {
    return undefined;
  }

  const normalized = normalizePrecoValor(Number(raw));
  if (!Number.isFinite(normalized)) {
    return undefined;
  }

  return roundPreco(normalized);
}

/** Monta params do sf_adicionar_carrinho. */
export function buildSfAdicionarCarrinhoParams(
  product: Product,
  cartToken: string | undefined,
  quantity: number,
  context?: SfCartProductContext,
): SfAdicionarCarrinhoParams {
  return {
    nome_produto: sfString(product.name) || sfString(product.label),
    marca: resolveProductMarca(product, context),
    // O catálogo não expõe vendedor/entregador por produto (não há distribuidor, filial ou
    // seller no modelo). Declarado explicitamente para deixar claro que a ausência é real —
    // o schema envia "" e o campo continua presente no payload, como o SF exige.
    vendido_entregue_por: undefined,
    detalhes_do_produto: sfString(product.description),
    valor: resolveProductValor(product),
    quantidade: quantity,
    cep: sfString(context?.deliveryCep),
    id_carrinho: sfString(cartToken),
  };
}

/** Monta params do sf_remover_carrinho. */
export function buildSfRemoverCarrinhoParams(
  product: Product,
  cartToken: string | undefined,
  quantity: number,
  context?: SfCartProductContext,
): SfRemoverCarrinhoParams {
  return {
    nome_produto: sfString(product.name) || sfString(product.label),
    marca: resolveProductMarca(product, context),
    codigo: sfString(product.sku) || sfString(product.id),
    // Mesma ausência do sf_adicionar_carrinho: sem vendedor/entregador no modelo de produto.
    vendido_entregue_por: undefined,
    preco_de: product.originalPrice
      ? roundPreco(normalizePrecoValor(Number(product.originalPrice)))
      : undefined,
    preco_por: resolveProductValor(product),
    quantidade: quantity,
    id_carrinho: sfString(cartToken),
  };
}

/** Monta params do sf_cadastro a partir do estado do fluxo de registro. */
export function buildSfCadastroParams(client: User, cpfCnpjFallback?: string): SfCadastroParams {
  const cpfCnpj = client.cpfCnpj || cpfCnpjFallback;
  const responsible = client.responsible;
  const birthday = responsible?.birthday
    ? (convertDateFromServer(responsible.birthday) as string) || responsible.birthday
    : undefined;

  return {
    razao_social: client.company?.name,
    cpf_cnpj: cpfCnpj,
    celular: responsible?.cellphone,
    email: responsible?.email,
    telefone_comercial: responsible?.comercialPhone || undefined,
    nome: responsible?.firstName,
    sobrenome: responsible?.lastName,
    data_nascimento: birthday,
  };
}

export interface SfCadastroEmpresaContext {
  areaAtuacao?: string;
  tipoEstabelecimento?: string;
}

const OPENING_STATUS_LABELS: Record<string, string> = {
  open: "Já está aberto",
  openingSoon: "Abrirá em breve",
  openingLater: "Abrirá mais tarde",
};

function resolveBusinessFromKey(businessKey?: string): { area?: string; tipo?: string } {
  if (!businessKey) {
    return {};
  }
  const keyEntry = KEYS.find((k) => k.key === businessKey);
  if (!keyEntry) {
    return {};
  }
  const business = businesses.find((b) => b.id === keyEntry.description);
  return {
    area: business?.industryType,
    tipo: business?.description,
  };
}

function formatWeekDays(days?: WeekDay[]): string | undefined {
  if (!days?.length) {
    return undefined;
  }
  return days
    .map((day) => WEEK_DAYS.find((d) => d.day === day)?.label || day)
    .join(", ");
}

function formatShifts(shifts?: Shift[]): string | undefined {
  if (!shifts?.length) {
    return undefined;
  }
  return shifts
    .map((shift) => SHIFTS.find((s) => s.shift === shift)?.label || shift)
    .join(", ");
}

/** Schema ajustado: documentos são Text ("sim" quando enviado). */
function sfDocumentoEnviado(file?: string): string | undefined {
  return file ? "sim" : undefined;
}

/** Monta params do sf_cadastro_empresa a partir do cliente no registro. */
export function buildSfCadastroEmpresaParams(
  client: User,
  context?: SfCadastroEmpresaContext,
): SfCadastroEmpresaParams {
  const company = client.company;
  const address = company?.address;
  const logistic = client.logistic;
  const proof = client.proof;
  const resolved = resolveBusinessFromKey(company?.businessKey);

  const streetNumber = address?.streetNumber;
  const numero =
    streetNumber !== undefined && streetNumber !== null && streetNumber !== ""
      ? Number(streetNumber)
      : undefined;

  return {
    nome_estabelecimento: company?.tradingName || company?.name,
    area_atuacao: context?.areaAtuacao || resolved.area,
    tipo_estabelecimento: context?.tipoEstabelecimento || resolved.tipo,
    cep: address?.postalCode,
    endereco_de_entrega: address?.street,
    numero: numero !== undefined && !isNaN(numero) ? numero : undefined,
    complemento: address?.complement,
    ponto_de_referencia: address?.reference,
    bairro: address?.neighborhood,
    cidade: address?.city,
    estado: address?.state,
    identidade: sfDocumentoEnviado(proof?.identification),
    comprovante_de_endereco: sfDocumentoEnviado(proof?.addressProof),
    estabelecimento_fachada: sfDocumentoEnviado(proof?.storeFront),
    pedidos_feitos_por_app: logistic?.attendanceMode === "digital",
    status_estabelecimento: logistic?.openingStatus
      ? OPENING_STATUS_LABELS[logistic.openingStatus] || logistic.openingStatus
      : undefined,
    dias_disponiveis_recebimento: formatWeekDays(logistic?.availableDays),
    horarios_disponiveis_entrega: formatShifts(logistic?.availableShifts),
  };
}
